import { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Baby, Plus, Phone, Trash2, ShieldAlert, CheckCircle2, ChevronDown, ChevronUp, Camera, Eye, EyeOff } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

const LOGO = "https://content.webfactorysite.co.uk/20260113092111-Wales-Event-Logo.png?t=1768296071";

const EMPTY_FORM = { name: "", dob: "", clothing: "", parent_names: "", parent_phones: "", event_location: "", occurred_date: "" };

const todayLondon = () => new Date().toLocaleDateString("en-CA", { timeZone: "Europe/London" });

const SAFEGUARDING_OPTIONS = [
  { value: "", label: "Not assessed" },
  { value: "all_ok", label: "Happy / no concern" },
  { value: "concern", label: "Concern noted" },
  { value: "referred", label: "Safeguarding referral made" },
];

const inputCls =
  "w-full h-11 rounded-lg border border-slate-300 bg-white px-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#005EB8]";

function safeguardingLabel(v) {
  return SAFEGUARDING_OPTIONS.find((o) => o.value === v)?.label || "Not assessed";
}

export default function LostChildren() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [events, setEvents] = useState([]);
  const [form, setForm] = useState({ ...EMPTY_FORM, occurred_date: todayLondon() });
  const [statusFilter, setStatusFilter] = useState("all");
  const [eventFilter, setEventFilter] = useState("all");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [openId, setOpenId] = useState(null);
  const [drafts, setDrafts] = useState({});
  const [viewPhoto, setViewPhoto] = useState("");
  const photoInputs = useRef({});

  const load = useCallback(async () => {
    try {
      const params = {};
      if (statusFilter !== "all") params.status = statusFilter;
      if (eventFilter !== "all") params.event = eventFilter;
      const { data } = await api.get("/lost-children", { params });
      setItems(data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, [statusFilter, eventFilter]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    api.get("/events").then(({ data }) => setEvents(data.filter((e) => !e.archived))).catch(() => {});
  }, []);

  const totals = {
    total: items.length,
    with_us: items.filter((c) => c.status === "with_us").length,
    reunited: items.filter((c) => c.status === "reunited").length,
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) return toast.error("Child's name (or description) is required");
    setSaving(true);
    try {
      await api.post("/lost-children", form);
      toast.success("Lost child record added");
      setForm({ ...EMPTY_FORM, occurred_date: todayLondon() });
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const patch = async (id, body, successMsg) => {
    try {
      await api.patch(`/lost-children/${id}`, body);
      if (successMsg) toast.success(successMsg);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const saveReunite = async (c) => {
    const d = drafts[c.id] || {};
    await patch(c.id, {
      reunite_checks: d.reunite_checks ?? c.reunite_checks,
      safeguarding_status: d.safeguarding_status ?? c.safeguarding_status,
      safeguarding_notes: d.safeguarding_notes ?? c.safeguarding_notes,
      occurred_date: d.occurred_date ?? c.occurred_date,
    }, "Details saved");
  };

  const remove = async (c) => {
    if (!window.confirm(`Delete the record for "${c.name}"? This cannot be undone.`)) return;
    try {
      await api.delete(`/lost-children/${c.id}`);
      toast.success("Record deleted");
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const setDraft = (id, key, value) =>
    setDrafts((prev) => ({ ...prev, [id]: { ...(prev[id] || {}), [key]: value } }));

  const uploadPhoto = async (c, file) => {
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      await api.post(`/lost-children/${c.id}/photo`, fd);
      toast.success("Photo added — viewable in-app only");
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const showPhoto = async (c) => {
    try {
      const res = await api.get(`/lost-children/${c.id}/photo-image`, { responseType: "blob" });
      setViewPhoto((old) => {
        if (old) URL.revokeObjectURL(old);
        return URL.createObjectURL(res.data);
      });
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const wipePhoto = async (c) => {
    if (!window.confirm("Wipe this photo permanently? (data protection)")) return;
    try {
      await api.delete(`/lost-children/${c.id}/photo`);
      toast.success("Photo wiped");
      setViewPhoto("");
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const groupedItems = items.reduce((acc, c) => {
    const k = c.event_location || "";
    (acc[k] = acc[k] || []).push(c);
    return acc;
  }, {});

  return (
    <div className="min-h-screen bg-[#F4F7FA]" data-testid="lost-children-page">
      <header className="sticky top-0 z-40 bg-white/90 backdrop-blur-md border-b border-slate-200 px-4 sm:px-8 py-3 flex justify-between items-center shadow-sm">
        <div className="flex items-center gap-3">
          <img src={LOGO} alt="WEMS" className="h-9 w-auto" />
          <div className="hidden sm:block">
            <p className="font-heading font-bold text-[#0F2547] text-sm leading-tight">Lost Children</p>
            <p className="text-[11px] text-slate-500">Safeguarding & Reunification Log</p>
          </div>
        </div>
        <Link
          to="/dashboard"
          data-testid="back-to-dashboard-link"
          className="inline-flex items-center gap-2 text-xs sm:text-sm font-semibold px-3 sm:px-4 py-2.5 rounded-lg border border-slate-300 text-[#0F2547] hover:border-[#005EB8] hover:text-[#005EB8] transition-colors min-h-[44px]"
        >
          <ArrowLeft className="h-4 w-4" /> <span className="hidden sm:inline">Dashboard</span>
        </Link>
      </header>

      <main className="max-w-5xl mx-auto p-4 sm:p-8">
        <div className="grid grid-cols-3 gap-4 mb-8" data-testid="lost-children-stats">
          <div className="bg-[#0F2547] rounded-xl p-5 text-white">
            <p className="text-xs font-semibold uppercase tracking-wider text-sky-300 mb-1">Total</p>
            <p className="font-vitals text-3xl font-extrabold" data-testid="stat-lost-total">{totals.total}</p>
          </div>
          <div className="bg-white rounded-xl border border-slate-200 p-5">
            <p className="text-xs font-semibold uppercase tracking-wider text-slate-400 mb-1 flex items-center gap-1"><Baby className="h-3.5 w-3.5 text-[#005EB8]" /> With us now</p>
            <p className="font-vitals text-3xl font-extrabold text-[#005EB8]" data-testid="stat-lost-with-us">{totals.with_us}</p>
          </div>
          <div className="bg-white rounded-xl border border-slate-200 p-5">
            <p className="text-xs font-semibold uppercase tracking-wider text-slate-400 mb-1 flex items-center gap-1"><CheckCircle2 className="h-3.5 w-3.5 text-emerald-600" /> Reunited</p>
            <p className="font-vitals text-3xl font-extrabold text-emerald-600" data-testid="stat-lost-reunited">{totals.reunited}</p>
          </div>
        </div>

        <form onSubmit={submit} className="bg-white rounded-xl border border-slate-200 p-5 mb-8" data-testid="lost-child-form">
          <h2 className="font-heading text-base font-bold text-[#0F2547] mb-4 flex items-center gap-2">
            <Plus className="h-4 w-4" /> Log a lost child
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-semibold text-slate-500 mb-1 block">Child's name or description *</label>
              <input data-testid="lost-child-name-input" className={inputCls} value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="e.g. Mia, ~6 yrs, red coat" />
            </div>
            <div>
              <label className="text-xs font-semibold text-slate-500 mb-1 block">Date of birth (approx. if unsure)</label>
              <input data-testid="lost-child-dob-input" type="date" className={inputCls} value={form.dob}
                onChange={(e) => setForm({ ...form, dob: e.target.value })} />
            </div>
            <div className="sm:col-span-2">
              <label className="text-xs font-semibold text-slate-500 mb-1 block">Clothing / what they're wearing</label>
              <input data-testid="lost-child-clothing-input" className={inputCls} value={form.clothing}
                onChange={(e) => setForm({ ...form, clothing: e.target.value })} placeholder="e.g. Blue hoodie, jeans, white trainers" />
            </div>
            <div>
              <label className="text-xs font-semibold text-slate-500 mb-1 block">Parent / guardian name(s)</label>
              <input data-testid="lost-child-parent-names-input" className={inputCls} value={form.parent_names}
                onChange={(e) => setForm({ ...form, parent_names: e.target.value })} placeholder="e.g. Sarah & Tom Jones" />
            </div>
            <div>
              <label className="text-xs font-semibold text-slate-500 mb-1 block">Parent phone number(s)</label>
              <input data-testid="lost-child-parent-phones-input" className={inputCls} value={form.parent_phones}
                onChange={(e) => setForm({ ...form, parent_phones: e.target.value })} placeholder="e.g. 07700 900123" />
            </div>
            <div className="sm:col-span-2">
              <label className="text-xs font-semibold text-slate-500 mb-1 block">Event</label>
              <select data-testid="lost-child-event-select" className={inputCls} value={form.event_location}
                onChange={(e) => {
                  const v = e.target.value;
                  const ev = events.find((x) => x.name === v);
                  setForm({ ...form, event_location: v, occurred_date: (ev && ev.event_date) || form.occurred_date || todayLondon() });
                }}>
                <option value="">No event / general</option>
                {events.map((ev) => <option key={ev.id || ev.name} value={ev.name}>{ev.name}</option>)}
              </select>
            </div>
            <div className="sm:col-span-2">
              <label className="text-xs font-semibold text-slate-500 mb-1 block">Date it happened</label>
              <input data-testid="lost-child-occurred-date-input" type="date" className={inputCls} value={form.occurred_date}
                onChange={(e) => setForm({ ...form, occurred_date: e.target.value })} />
              <p className="text-[11px] text-slate-400 mt-1">Auto-filled from the event's date — change it if you're logging something from a previous day. This controls which day's report it appears on.</p>
            </div>
          </div>
          <button type="submit" disabled={saving} data-testid="lost-child-submit-button"
            className="mt-4 inline-flex items-center justify-center gap-2 h-12 px-5 rounded-lg bg-[#005EB8] hover:bg-[#004a93] transition-colors text-white font-semibold text-sm disabled:opacity-60 w-full sm:w-auto">
            <Plus className="h-4 w-4" /> {saving ? "Saving…" : "Add record"}
          </button>
        </form>

        <div className="flex flex-col sm:flex-row gap-3 mb-4">
          <select data-testid="lost-status-filter" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className={inputCls}>
            <option value="all">All records</option>
            <option value="with_us">Still with us</option>
            <option value="reunited">Reunited</option>
          </select>
          <select data-testid="lost-event-filter" value={eventFilter} onChange={(e) => setEventFilter(e.target.value)} className={inputCls}>
            <option value="all">All events</option>
            {events.map((ev) => <option key={ev.id || ev.name} value={ev.name}>{ev.name}</option>)}
          </select>
        </div>

        {loading ? (
          <p className="text-sm text-slate-400 py-8 text-center" data-testid="lost-loading">Loading…</p>
        ) : items.length === 0 ? (
          <div className="bg-white rounded-xl border border-slate-200 p-8 text-center" data-testid="lost-empty-state">
            <Baby className="h-8 w-8 text-slate-300 mx-auto mb-2" />
            <p className="text-sm text-slate-500 font-medium">No lost child records.</p>
          </div>
        ) : (
          <div data-testid="lost-children-list">
            {Object.entries(groupedItems).map(([evName, evItems]) => (
              <div key={evName || "general"} className="mb-6" data-testid={`lost-group-${(evName || "no-event").toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
                <h3 className="font-heading text-sm font-bold uppercase tracking-wider text-[#005EB8] mb-2 flex items-center gap-2 flex-wrap">
                  {evName || "No event / general"}
                  <span className="text-slate-400 font-vitals">{evItems.length}</span>
                  <span className="text-[10px] font-semibold text-slate-400 normal-case tracking-normal">
                    · {evItems.filter((x) => x.status === "with_us").length} with us · {evItems.filter((x) => x.status === "reunited").length} reunited
                  </span>
                </h3>
                <div className="space-y-3">
            {evItems.map((c) => { const idx = items.indexOf(c);
              const open = openId === c.id;
              const d = drafts[c.id] || {};
              return (
                <div key={c.id} className="bg-white rounded-xl border border-slate-200 p-5" data-testid={`lost-child-card-${idx}`}>
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <p className="font-heading font-bold text-[#0F2547]" data-testid={`lost-child-name-${idx}`}>{c.name}</p>
                        <span data-testid={`lost-child-status-${idx}`}
                          className={`text-[11px] font-bold px-2 py-0.5 rounded-full ${c.status === "reunited" ? "bg-emerald-100 text-emerald-700" : "bg-sky-100 text-[#005EB8]"}`}>
                          {c.status === "reunited" ? "Reunited" : "With us"}
                        </span>
                        {c.safeguarding_status && c.safeguarding_status !== "all_ok" && (
                          <span data-testid={`lost-child-safeguarding-flag-${idx}`}
                            className={`text-[11px] font-bold px-2 py-0.5 rounded-full inline-flex items-center gap-1 ${c.safeguarding_status === "referred" ? "bg-red-100 text-[#D5281B]" : "bg-amber-100 text-amber-700"}`}>
                            <ShieldAlert className="h-3 w-3" /> {safeguardingLabel(c.safeguarding_status)}
                          </span>
                        )}
                      </div>
                      <p className="text-xs text-slate-500 mt-1">
                        {c.occurred_date && <span className="font-semibold text-[#0F2547]">Date: {new Date(`${c.occurred_date}T00:00:00`).toLocaleDateString("en-GB", { weekday: "short", day: "numeric", month: "short" })} · </span>}
                        {c.dob && <>DOB {c.dob} · </>}
                        {c.clothing && <>{c.clothing} · </>}
                        {c.event_location || "No event"}
                      </p>
                      {(c.parent_names || c.parent_phones) && (
                        <p className="text-xs text-slate-500 mt-0.5 flex items-center gap-1">
                          <Phone className="h-3 w-3" /> {c.parent_names} {c.parent_names && c.parent_phones && "—"} {c.parent_phones}
                        </p>
                      )}
                      <p className="text-[11px] text-slate-400 mt-1">
                        Logged by {c.created_by_name} · {c.created_at ? new Date(c.created_at).toLocaleString("en-GB", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" }) : ""}
                        {c.reunited_at && <> · Reunited {new Date(c.reunited_at).toLocaleString("en-GB", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" })}</>}
                      </p>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      {c.status === "with_us" ? (
                        <button data-testid={`lost-child-reunite-btn-${idx}`}
                          onClick={() => patch(c.id, { status: "reunited" }, "Marked as reunited")}
                          className="text-xs font-bold px-3 py-2 rounded-lg bg-emerald-600 text-white hover:bg-emerald-700 transition-colors min-h-[36px]">
                          Mark reunited
                        </button>
                      ) : (
                        <button data-testid={`lost-child-reopen-btn-${idx}`}
                          onClick={() => patch(c.id, { status: "with_us" }, "Moved back to with us")}
                          className="text-xs font-bold px-3 py-2 rounded-lg border border-slate-300 text-slate-500 hover:border-[#005EB8] hover:text-[#005EB8] transition-colors min-h-[36px]">
                          Re-open
                        </button>
                      )}
                      <button data-testid={`lost-child-expand-btn-${idx}`} onClick={() => setOpenId(open ? null : c.id)}
                        className="p-2 rounded-lg border border-slate-300 text-slate-500 hover:border-[#005EB8] hover:text-[#005EB8] transition-colors min-h-[36px]">
                        {open ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                      </button>
                      {user?.role === "admin" && (
                        <button data-testid={`lost-child-delete-btn-${idx}`} onClick={() => remove(c)}
                          className="p-2 rounded-lg border border-slate-300 text-slate-400 hover:border-[#D5281B] hover:text-[#D5281B] transition-colors min-h-[36px]">
                          <Trash2 className="h-4 w-4" />
                        </button>
                      )}
                    </div>
                  </div>

                  {open && (
                    <div className="mt-4 pt-4 border-t border-slate-100 space-y-3" data-testid={`lost-child-details-${idx}`}>
                      <div>
                        <label className="text-xs font-semibold text-slate-500 mb-1 block">Date it happened</label>
                        <input type="date" data-testid={`lost-child-occurred-edit-${idx}`} className={inputCls}
                          value={d.occurred_date ?? c.occurred_date}
                          onChange={(e) => setDraft(c.id, "occurred_date", e.target.value)} />
                      </div>
                      <div>
                        <label className="text-xs font-semibold text-slate-500 mb-1 block">
                          Checks performed before release to parent/guardian
                        </label>
                        <textarea data-testid={`lost-child-checks-input-${idx}`} rows={2}
                          className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#005EB8]"
                          placeholder="e.g. Verified ID, confirmed child recognises adult, contacted event control, second staff member present…"
                          value={d.reunite_checks ?? c.reunite_checks}
                          onChange={(e) => setDraft(c.id, "reunite_checks", e.target.value)} />
                      </div>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        <div>
                          <label className="text-xs font-semibold text-slate-500 mb-1 block">Safeguarding status</label>
                          <select data-testid={`lost-child-safeguarding-select-${idx}`} className={inputCls}
                            value={d.safeguarding_status ?? c.safeguarding_status}
                            onChange={(e) => setDraft(c.id, "safeguarding_status", e.target.value)}>
                            {SAFEGUARDING_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                          </select>
                        </div>
                        <div>
                          <label className="text-xs font-semibold text-slate-500 mb-1 block">Safeguarding notes</label>
                          <input data-testid={`lost-child-safeguarding-notes-${idx}`} className={inputCls}
                            placeholder="Optional notes / referral details"
                            value={d.safeguarding_notes ?? c.safeguarding_notes}
                            onChange={(e) => setDraft(c.id, "safeguarding_notes", e.target.value)} />
                        </div>
                      </div>
                      <div>
                        <label className="text-xs font-semibold text-slate-500 mb-1 block">Photo (helps staff spot them — in-app view only, wiped on reunite)</label>
                        <input type="file" accept="image/*" capture="environment" className="hidden"
                          ref={(el) => { photoInputs.current[c.id] = el; }}
                          data-testid={`lost-child-photo-input-${idx}`}
                          onChange={(e) => { uploadPhoto(c, e.target.files[0]); e.target.value = ""; }} />
                        <div className="flex items-center gap-2">
                          <button onClick={() => photoInputs.current[c.id]?.click()} data-testid={`lost-child-photo-upload-${idx}`}
                            className="inline-flex items-center gap-1.5 text-xs font-bold text-[#005EB8] border border-slate-300 hover:border-[#005EB8] rounded-lg px-3 py-2 transition-colors min-h-[36px]">
                            <Camera className="h-3.5 w-3.5" /> {c.has_photo ? "Replace photo" : "Add photo"}
                          </button>
                          {c.has_photo && (
                            <>
                              <button onClick={() => showPhoto(c)} data-testid={`lost-child-photo-view-${idx}`}
                                className="inline-flex items-center gap-1.5 text-xs font-bold text-[#0F2547] border border-slate-300 hover:border-[#005EB8] rounded-lg px-3 py-2 transition-colors min-h-[36px]">
                                <Eye className="h-3.5 w-3.5" /> View
                              </button>
                              {user?.role === "admin" && (
                                <button onClick={() => wipePhoto(c)} data-testid={`lost-child-photo-wipe-${idx}`}
                                  className="inline-flex items-center gap-1.5 text-xs font-bold text-[#D5281B] border border-red-200 hover:bg-red-50 rounded-lg px-3 py-2 transition-colors min-h-[36px]">
                                  <EyeOff className="h-3.5 w-3.5" /> Wipe
                                </button>
                              )}
                            </>
                          )}
                        </div>
                      </div>
                      <button data-testid={`lost-child-save-details-btn-${idx}`} onClick={() => saveReunite(c)}
                        className="text-xs font-bold px-4 py-2.5 rounded-lg bg-[#0F2547] text-white hover:bg-[#173766] transition-colors min-h-[36px]">
                        Save details
                      </button>
                    </div>
                  )}
                </div>
              );
            })}
                </div>
              </div>
            ))}
          </div>
        )}
      </main>

      {viewPhoto && (
        <div className="fixed inset-0 z-50 bg-black/80 grid place-items-center p-4" onClick={() => setViewPhoto("")} data-testid="lost-photo-modal">
          <div className="bg-white rounded-2xl p-3 max-w-sm w-full" onClick={(e) => e.stopPropagation()}>
            <img src={viewPhoto} alt="Lost child" className="w-full rounded-xl" />
            <p className="text-[11px] text-slate-400 text-center mt-2">In-app view only — do not share or save. Wiped when the child is reunited.</p>
            <button onClick={() => setViewPhoto("")} data-testid="lost-photo-close"
              className="mt-2 w-full h-11 rounded-lg bg-[#0F2547] text-white text-sm font-semibold">Close</button>
          </div>
        </div>
      )}
    </div>
  );
}
